import React, { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import { LineChart } from "@mui/x-charts/LineChart";
import CatalogPageShell from "../components/Products/CatalogPageShell.jsx";
import { catalogPrimaryButtonSx } from "../components/Products/catalogGridStyles.js";
import PullDateRangeFields from "../components/Helpers/PullDateRangeFields.jsx";
import useApiData from "../services/useApiData.js";
import {
    BreakdownPanel,
    ChannelPill,
    ChannelTable,
    KpiCard,
    KpiGrid,
    ReportContent,
    ReportError,
    ReportLoading,
    ReportScroll,
    ReportSection,
    ReportToolbar,
    SpotlightStrip,
    formatCurrency,
    formatNumber,
    formatPercent,
} from "../components/Reporting/ReportPrimitives.jsx";

const CHART_PRIMARY = "#0d9488";

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    return { from: dayInputValue(start), to: dayInputValue(end) };
}

function formatRangeLabel(from, to) {
    const opts = { month: "short", day: "numeric", year: "numeric" };
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${to}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
    return `${start.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
}

function marginTone(value) {
    if (value == null || !Number.isFinite(Number(value))) return undefined;
    if (Number(value) > 0) return "positive";
    if (Number(value) < 0) return "negative";
    return undefined;
}

export default function ReportingOrders() {
    const initial = defaultRange();
    const [from, setFrom] = useState(initial.from);
    const [to, setTo] = useState(initial.to);
    const [applied, setApplied] = useState(initial);

    const url = `api/broylesllc/reports/orders?from=${encodeURIComponent(applied.from)}&to=${encodeURIComponent(applied.to)}`;
    const { data, error, load } = useApiData(url);

    const fulfillment = data?.fulfillment?.overall;
    const margin = data?.margin?.overall;
    const turnTime = data?.turnTime?.overall;
    const ordersOverTime = data?.ordersOverTime;
    const volumePoints = ordersOverTime?.points || [];

    const volumeStats = useMemo(() => {
        if (!volumePoints.length) {
            return { peak: null, avgPerBucket: null, granularityLabel: "day" };
        }
        let peak = volumePoints[0];
        let sum = 0;
        for (const point of volumePoints) {
            sum += point.orderCount || 0;
            if ((point.orderCount || 0) > (peak.orderCount || 0)) peak = point;
        }
        return {
            peak,
            avgPerBucket: sum / volumePoints.length,
            granularityLabel: ordersOverTime?.granularity === "week" ? "week" : "day",
        };
    }, [volumePoints, ordersOverTime?.granularity]);

    const chartLabels = useMemo(
        () => volumePoints.map((point) => point.label || point.date),
        [volumePoints]
    );
    const chartCounts = useMemo(
        () => volumePoints.map((point) => point.orderCount || 0),
        [volumePoints]
    );

    const fulfillmentRows = useMemo(() => {
        const byChannel = data?.fulfillment?.byChannel || {};
        return Object.entries(byChannel).map(([channel, stats]) => ({
            channel,
            ...stats,
        }));
    }, [data]);

    const marginRows = useMemo(() => {
        const byChannel = data?.margin?.byChannel || {};
        return Object.entries(byChannel).map(([channel, stats]) => ({
            channel,
            ...stats,
        }));
    }, [data]);

    const turnRows = useMemo(() => {
        const byChannel = data?.turnTime?.byChannel || {};
        return Object.entries(byChannel).map(([channel, stats]) => ({
            channel,
            ...stats,
        }));
    }, [data]);

    const applyRange = (event) => {
        event.preventDefault();
        setApplied({ from, to });
    };

    return (
        <CatalogPageShell
            title="Orders"
            subtitle="Order volume over time, plus fulfillment, margin, and turn time for marketplace orders in the selected range."
        >
            <ReportScroll>
                <form onSubmit={applyRange}>
                    <ReportToolbar>
                        <PullDateRangeFields
                            layout="row"
                            startDate={from}
                            endDate={to}
                            onStartChange={setFrom}
                            onEndChange={setTo}
                        />
                        <Button type="submit" variant="contained" size="small" sx={catalogPrimaryButtonSx}>
                            Apply range
                        </Button>
                        <div className="report-toolbar-spacer" />
                        <div className="report-toolbar-meta">
                            {formatRangeLabel(applied.from, applied.to)}
                        </div>
                    </ReportToolbar>
                </form>

                {error ? <ReportError message="Failed to load orders report." /> : null}

                {load && !data ? (
                    <ReportLoading label="Crunching fulfillment and margin…" />
                ) : data ? (
                    <ReportContent>
                        <SpotlightStrip
                            items={[
                                {
                                    label: "Orders",
                                    value: formatNumber(ordersOverTime?.totalOrders, 0),
                                    hint: `Placed in range · avg ${formatNumber(volumeStats.avgPerBucket, 1)} / ${volumeStats.granularityLabel}`,
                                },
                                {
                                    label: "Avg fulfillment",
                                    value: `${formatNumber(fulfillment?.avgHours)}h`,
                                    hint: `${formatNumber(fulfillment?.avgDays, 2)} days · ${formatNumber(fulfillment?.sampleSize, 0)} shipped`,
                                },
                                {
                                    label: "Margin",
                                    value: formatPercent(margin?.marginPct),
                                    hint: `${formatCurrency(margin?.margin)} net · ${formatCurrency(margin?.avgMargin)} avg`,
                                },
                            ]}
                        />

                        <ReportSection
                            eyebrow="Volume"
                            title="Orders over time"
                            subtitle={
                                ordersOverTime?.granularity === "week"
                                    ? "Unique marketplace orders by week (ranges over 90 days)."
                                    : "Unique marketplace orders by day for the selected range."
                            }
                        >
                            <KpiGrid>
                                <KpiCard
                                    label="Total orders"
                                    value={formatNumber(ordersOverTime?.totalOrders, 0)}
                                    hint="Unique order numbers"
                                    tone="accent"
                                />
                                <KpiCard
                                    label={`Avg / ${volumeStats.granularityLabel}`}
                                    value={formatNumber(volumeStats.avgPerBucket, 1)}
                                    hint="Across the full range"
                                />
                                <KpiCard
                                    label="Peak"
                                    value={formatNumber(volumeStats.peak?.orderCount, 0)}
                                    hint={volumeStats.peak?.label || "—"}
                                />
                                <KpiCard
                                    label="Buckets"
                                    value={formatNumber(volumePoints.length, 0)}
                                    hint={
                                        ordersOverTime?.granularity === "week"
                                            ? "Weekly points"
                                            : "Daily points"
                                    }
                                />
                            </KpiGrid>
                            <div className="report-panel">
                                <div className="report-panel-body">
                                    {volumePoints.length === 0 ? (
                                        <p className="report-chart-empty">
                                            No orders in this range.
                                        </p>
                                    ) : (
                                        <div className="report-chart">
                                            <LineChart
                                                height={300}
                                                hideLegend
                                                series={[
                                                    {
                                                        data: chartCounts,
                                                        label: "Orders",
                                                        color: CHART_PRIMARY,
                                                        area: true,
                                                        showMark: chartCounts.length <= 45,
                                                        curve: "linear",
                                                    },
                                                ]}
                                                xAxis={[
                                                    {
                                                        data: chartLabels,
                                                        scaleType: "point",
                                                        tickLabelStyle: {
                                                            fontSize: 11,
                                                            fill: "#5c6570",
                                                        },
                                                    },
                                                ]}
                                                yAxis={[
                                                    {
                                                        min: 0,
                                                        tickLabelStyle: {
                                                            fontSize: 11,
                                                            fill: "#5c6570",
                                                        },
                                                        valueFormatter: (value) =>
                                                            formatNumber(value, 0),
                                                    },
                                                ]}
                                                grid={{ horizontal: true }}
                                                margin={{ left: 16, right: 16, top: 20, bottom: 8 }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </ReportSection>

                        <ReportSection
                            eyebrow="Speed"
                            title="Fulfillment time"
                            subtitle="For orders placed in this range: time from order date to label shipped."
                        >
                            <KpiGrid>
                                <KpiCard
                                    label="Average"
                                    value={`${formatNumber(fulfillment?.avgHours)}h`}
                                    hint={`${formatNumber(fulfillment?.avgDays, 2)} days`}
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Median"
                                    value={`${formatNumber(fulfillment?.medianHours)}h`}
                                    hint={`${formatNumber(fulfillment?.medianDays, 2)} days`}
                                />
                                <KpiCard
                                    label="P90"
                                    value={`${formatNumber(fulfillment?.p90Hours)}h`}
                                    hint="Slowest tenth of shipments"
                                />
                                <KpiCard
                                    label="Shipped"
                                    value={formatNumber(fulfillment?.sampleSize, 0)}
                                    hint="Orders placed in range with a shipment"
                                />
                            </KpiGrid>
                            <BreakdownPanel title="By selling channel">
                                <ChannelTable
                                    rows={fulfillmentRows}
                                    emptyLabel="No shipped orders placed in this range."
                                    columns={[
                                        {
                                            key: "channel",
                                            label: "Channel",
                                            render: (row) => <ChannelPill channel={row.channel} />,
                                        },
                                        {
                                            key: "avgHours",
                                            label: "Avg",
                                            align: "right",
                                            primary: true,
                                            render: (row) => `${formatNumber(row.avgHours)}h`,
                                        },
                                        {
                                            key: "medianHours",
                                            label: "Median",
                                            align: "right",
                                            muted: true,
                                            render: (row) => `${formatNumber(row.medianHours)}h`,
                                        },
                                        {
                                            key: "sampleSize",
                                            label: "Count",
                                            align: "right",
                                            muted: true,
                                            render: (row) => formatNumber(row.sampleSize, 0),
                                        },
                                    ]}
                                />
                            </BreakdownPanel>
                        </ReportSection>

                        <ReportSection
                            eyebrow="Profit"
                            title="Margin analysis"
                            subtitle="Booked gross from pulled marketplace orders in range, after platform fees, minus item cost and packing/label spend."
                        >
                            <KpiGrid>
                                <KpiCard
                                    label="Revenue"
                                    value={formatCurrency(margin?.revenue)}
                                    hint="Gross from pulled orders (order date)"
                                />
                                <KpiCard
                                    label="Net margin"
                                    value={formatCurrency(margin?.margin)}
                                    hint={`Avg ${formatCurrency(margin?.avgMargin)} / order`}
                                    tone={marginTone(margin?.margin)}
                                />
                                <KpiCard
                                    label="Margin rate"
                                    value={formatPercent(margin?.marginPct)}
                                    hint="Of gross revenue"
                                    tone={marginTone(margin?.marginPct)}
                                />
                                <KpiCard
                                    label="COGS + ship"
                                    value={formatCurrency(
                                        (margin?.cogs || 0) + (margin?.shippingCost || 0)
                                    )}
                                    hint={`COGS ${formatCurrency(margin?.cogs)} · ship ${formatCurrency(margin?.shippingCost)}`}
                                />
                            </KpiGrid>
                            <BreakdownPanel title="By selling channel">
                                <ChannelTable
                                    rows={marginRows}
                                    emptyLabel="No margin data in this range."
                                    columns={[
                                        {
                                            key: "channel",
                                            label: "Channel",
                                            render: (row) => <ChannelPill channel={row.channel} />,
                                        },
                                        {
                                            key: "revenue",
                                            label: "Revenue",
                                            align: "right",
                                            muted: true,
                                            render: (row) => formatCurrency(row.revenue),
                                        },
                                        {
                                            key: "margin",
                                            label: "Margin",
                                            align: "right",
                                            primary: true,
                                            render: (row) => formatCurrency(row.margin),
                                        },
                                        {
                                            key: "marginPct",
                                            label: "Rate",
                                            align: "right",
                                            render: (row) => formatPercent(row.marginPct),
                                        },
                                        {
                                            key: "orderCount",
                                            label: "Orders",
                                            align: "right",
                                            muted: true,
                                            render: (row) => formatNumber(row.orderCount, 0),
                                        },
                                    ]}
                                />
                            </BreakdownPanel>
                        </ReportSection>

                        <ReportSection
                            eyebrow="Inventory velocity"
                            title="Turn time"
                            subtitle="Days from purchase to sale for items on orders placed in this range."
                        >
                            <KpiGrid>
                                <KpiCard
                                    label="Average turn"
                                    value={`${formatNumber(turnTime?.avgTurnDays)}d`}
                                    hint="Purchase → sale"
                                    tone="accent"
                                />
                                <KpiCard
                                    label="Median"
                                    value={`${formatNumber(turnTime?.medianTurnDays)}d`}
                                    hint="Typical item"
                                />
                                <KpiCard
                                    label="P90"
                                    value={`${formatNumber(turnTime?.p90TurnDays)}d`}
                                    hint="Slowest tenth"
                                />
                                <KpiCard
                                    label="Purchase → process"
                                    value={`${formatNumber(turnTime?.avgPurchaseToProcessDays)}d`}
                                    hint={`Sale path ${formatNumber(turnTime?.avgPurchasingToSaleDays)}d`}
                                />
                            </KpiGrid>
                            <BreakdownPanel title="By channel / platform">
                                <ChannelTable
                                    rows={turnRows}
                                    emptyLabel="No turn-time samples in this range."
                                    columns={[
                                        {
                                            key: "channel",
                                            label: "Channel",
                                            render: (row) => <ChannelPill channel={row.channel} />,
                                        },
                                        {
                                            key: "avgTurnDays",
                                            label: "Avg turn",
                                            align: "right",
                                            primary: true,
                                            render: (row) => `${formatNumber(row.avgTurnDays)}d`,
                                        },
                                        {
                                            key: "medianTurnDays",
                                            label: "Median",
                                            align: "right",
                                            muted: true,
                                            render: (row) => `${formatNumber(row.medianTurnDays)}d`,
                                        },
                                        {
                                            key: "sampleSize",
                                            label: "Items",
                                            align: "right",
                                            muted: true,
                                            render: (row) => formatNumber(row.sampleSize, 0),
                                        },
                                    ]}
                                />
                            </BreakdownPanel>
                        </ReportSection>
                    </ReportContent>
                ) : null}
            </ReportScroll>
        </CatalogPageShell>
    );
}
